import { ApiError } from "./api";

/**
 * The 422 every target-writing route returns when the buyer's location cannot
 * be placed (plan 454, tech spec 278 §API Contracts):
 *
 *   { "code": "target_unresolvable", "reason": "no_match" | "ambiguous" | "no_gazetteer_row" }
 *
 * `PUT /api/readiness/target`, `POST /api/onboarding`, and the property update
 * that re-targets all return this exact body. It never echoes the label, so the
 * copy below is composed from the label the CLIENT sent.
 */
export const TARGET_UNRESOLVABLE_CODE = "target_unresolvable";

export type TargetUnresolvableReason =
  | "no_match"
  | "ambiguous"
  | "no_gazetteer_row";

const REASONS: readonly TargetUnresolvableReason[] = [
  "no_match",
  "ambiguous",
  "no_gazetteer_row",
];

/** Thrown by `useTargetEdit` (which speaks raw `fetch`, not `ApiError`). */
export class TargetUnresolvableError extends Error {
  readonly code = TARGET_UNRESOLVABLE_CODE;
  constructor(public readonly reason: TargetUnresolvableReason) {
    super(`target unresolvable: ${reason}`);
    this.name = "TargetUnresolvableError";
  }
}

/** Narrows an arbitrary body value to a known reason; unknown -> no_match. */
export function parseTargetUnresolvableReason(
  value: unknown,
): TargetUnresolvableReason {
  return REASONS.find((reason) => reason === value) ?? "no_match";
}

/**
 * The reason when `error` is a `target_unresolvable` 422 (from either error
 * type), else null. A server fault - including an unwired resolver, which is a
 * 5xx - is deliberately null: it is not the buyer's input, so it must keep
 * rendering the caller's generic "couldn't save" copy.
 */
export function targetUnresolvableReason(
  error: unknown,
): TargetUnresolvableReason | null {
  if (error instanceof TargetUnresolvableError) {
    return error.reason;
  }
  if (
    error instanceof ApiError &&
    error.status === 422 &&
    error.structured?.code === TARGET_UNRESOLVABLE_CODE
  ) {
    return parseTargetUnresolvableReason(error.structured.reason);
  }
  return null;
}

/**
 * Plain-language copy for each reason (product spec 277, A6). With no label
 * (a property address, or a surface that no longer has the typed text) the
 * `{label}` sentences fall back to "that area" so nothing reads as a template.
 */
export function targetUnresolvableMessage(
  reason: TargetUnresolvableReason,
  label?: string | null,
): string {
  const named = label?.trim();
  switch (reason) {
    case "no_match":
      return named
        ? `We couldn't find '${named}'. Pick a suggestion or enter a ZIP.`
        : "We couldn't find that area. Pick a suggestion or enter a ZIP.";
    case "ambiguous":
      return named
        ? `Which ${named}? Add the state or pick a suggestion.`
        : "Which area did you mean? Add the state or pick a suggestion.";
    case "no_gazetteer_row":
      return "We can't place that area yet. Try a nearby city or ZIP.";
  }
}

/**
 * The inline message for a failed target save: the reason copy for a
 * `target_unresolvable` refusal, else `fallback` unchanged.
 */
export function targetSaveErrorMessage(
  error: unknown,
  label: string | null | undefined,
  fallback: string,
): string {
  const reason = targetUnresolvableReason(error);
  return reason ? targetUnresolvableMessage(reason, label) : fallback;
}
